import React, { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../services/apiClient';
import { useAuth } from '../context/AuthContext';

// Checklists on a shop order (Pat, Sep 23 2026) — "most of the time we call them a load
// up list". Any number per order, each with a title and lines you tick off. The shop
// crew, supervisors, PMs and the office can all add and tick; it saves as you go.
// Two check marks a line (Ryan, Oct 27 2026): the shop's (loaded) and the PM / field supervisor's (they
// have it). Executives tick either; the office ticks the shop's. A saved load-up list (Settings →
// Checklists & Load-up Lists) can be dropped in whole, and a list's lines can go on a PO ("🧾 Create
// PO" — tick what the shop has to buy). On a new order the lists are drafts (DraftLists) until Save.
const EXEC_ROLES = ['admin', 'Admin', 'Executive'];
export const canTickShop = role => EXEC_ROLES.includes(role) || ['ShopWorker', 'Accounting', 'OfficeStaff'].includes(role);
export const canTickField = role => EXEC_ROLES.includes(role) || ['ProjectManager', 'FieldSupervisor'].includes(role);
const btn = { padding: '6px 11px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', fontSize: 12.5, fontWeight: 700, color: '#374151', cursor: 'pointer', whiteSpace: 'nowrap' };
const inp = { border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 10px', fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box', width: '100%' };
// The signed-in role: the session's first (the app routes by it, and the server checks it), else the worker record.
function useRole() {
  const { worker } = useAuth() || {};
  return (() => { try { return JSON.parse(localStorage.getItem('titan_session') || 'null')?.role; } catch { return null; } })() || worker?.role;
}
const splitLines = text => String(text || '').split(/\r?\n/).map(s => s.trim()).filter(Boolean);

// The saved lists on an order. onCreatePo(lineTexts, done) opens the parent's PO window; done(po) marks
// the lines with the PO number. showAdd: the old add buttons (orders without the load-up tiles).
export default function ShopChecklists({ orderId, canEdit = true, reloadKey = 0, onCreatePo, showAdd = true }) {
  const role = useRole();
  const [lists, setLists] = useState(null);
  const [err, setErr] = useState('');
  const [picking, setPicking] = useState(false);
  const load = useCallback(() => {
    if (!orderId) return;
    apiFetch(`/shop-orders/${orderId}/checklists`).then(r => setLists(Array.isArray(r) ? r : [])).catch(() => setLists([]));
  }, [orderId]);
  useEffect(() => { load(); }, [load, reloadKey]);
  async function insert(tpl) {
    setPicking(false);
    try { await apiFetch(`/shop-orders/${orderId}/checklists`, { method: 'POST', body: JSON.stringify({ template_id: tpl.id }) }); load(); }
    catch (e) { setErr(e?.error || e?.message || 'Could not add that list.'); }
  }
  async function addList() {
    const title = window.prompt('Name the list:', 'Load Up List');
    if (title == null) return;
    try { await apiFetch(`/shop-orders/${orderId}/checklists`, { method: 'POST', body: JSON.stringify({ title: title.trim() || 'Load Up List' }) }); load(); }
    catch (e) { setErr(e?.error || e?.message || 'Could not add the list.'); }
  }
  if (!orderId) return null;
  if (lists === null) return <div style={{ fontSize: 12.5, color: '#9ca3af' }}>Loading checklists…</div>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {lists.map(l => {
        const base = `/shop-orders/${orderId}/checklists/${l.id}`;
        const call = async (path, opts, what) => { try { await apiFetch(base + path, opts); load(); } catch (e) { setErr(e?.error || e?.message || what); } };
        return (
          <ListCard key={l.id} title={l.title} items={l.items || []} role={role} canEdit={canEdit}
            onRename={t => call('', { method: 'PATCH', body: JSON.stringify({ title: t }) }, 'Could not rename it.')}
            onRemove={() => call('', { method: 'DELETE' }, 'Could not remove it.')}
            onAddLines={lines => call('/items', { method: 'POST', body: JSON.stringify({ lines }) }, 'That line did not save.')}
            onEditLine={(it, text) => call(`/items/${it.id}`, { method: 'PATCH', body: JSON.stringify({ text }) }, 'Could not save that.')}
            onDelLine={it => call(`/items/${it.id}`, { method: 'DELETE' }, 'Could not remove that.')}
            onToggle={(it, which) => call(`/items/${it.id}`, { method: 'PATCH', body: JSON.stringify({ [which]: !it[which] }) }, 'Could not save that.')}
            onCreatePo={onCreatePo && canEdit ? picked => onCreatePo(picked.map(i => i.text), async po => {
              for (const it of picked) { try { await apiFetch(`${base}/items/${it.id}`, { method: 'PATCH', body: JSON.stringify({ po_number: po.po_number }) }); } catch {} }
              load();
            }) : null} />
        );
      })}
      {err && <div style={{ fontSize: 12.5, color: '#dc2626', fontWeight: 700 }}>{err}</div>}
      {canEdit && showAdd && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', position: 'relative' }}>
          <button onClick={() => setPicking(p => !p)} style={{ ...btn, border: '1.5px solid #c4b5fd', background: '#f5f3ff', color: '#6d28d9' }}>📋 Insert a saved load-up list</button>
          <button onClick={addList} style={{ ...btn, border: '1.5px dashed #c4b5fd', background: '#faf5ff', color: '#6d28d9' }}>
            ＋ Add a checklist{lists.length === 0 ? ' (load up list)' : ''}
          </button>
          {picking && <LoadUpPicker onPick={insert} onClose={() => setPicking(false)} />}
        </div>
      )}
      {!canEdit && lists.length === 0 && <div style={{ fontSize: 12.5, color: '#9ca3af' }}>No checklists on this order.</div>}
    </div>
  );
}

// A new order's lists before Save: [{ key, title, template_id, lines: [{ key, text, po_number }] }].
// Same card, edited in place; the check marks open up once the order is saved.
export function DraftLists({ lists, setLists, onCreatePo }) {
  const role = useRole();
  const up = (key, f) => setLists(ls => ls.map(l => (l.key === key ? f(l) : l)));
  const k = () => Math.random().toString(36).slice(2);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {lists.map(l => (
        <ListCard key={l.key} draft title={l.title} role={role} canEdit items={l.lines.map(x => ({ ...x, id: x.key }))}
          onRename={t => up(l.key, x => ({ ...x, title: t }))}
          onRemove={() => setLists(ls => ls.filter(x => x.key !== l.key))}
          onAddLines={lines => up(l.key, x => ({ ...x, lines: [...x.lines, ...lines.map(text => ({ key: k(), text }))] }))}
          onEditLine={(it, text) => up(l.key, x => ({ ...x, lines: x.lines.map(y => (y.key === it.id ? { ...y, text } : y)) }))}
          onDelLine={it => up(l.key, x => ({ ...x, lines: x.lines.filter(y => y.key !== it.id) }))}
          onCreatePo={onCreatePo ? picked => onCreatePo(picked.map(i => i.text), po => {
            const ids = new Set(picked.map(i => i.id));
            up(l.key, x => ({ ...x, lines: x.lines.map(y => (ids.has(y.key) ? { ...y, po_number: po.po_number } : y)) }));
          }) : null} />
      ))}
    </div>
  );
}
export const draftFromTemplate = tpl => ({ key: Math.random().toString(36).slice(2), title: tpl.title, template_id: tpl.id, lines: (tpl.items || []).map(text => ({ key: Math.random().toString(36).slice(2), text })) });
export const blankDraft = () => ({ key: Math.random().toString(36).slice(2), title: 'Load-up List', template_id: null, lines: [] });

// The three tiles where a Deliver Materials order says what it is (Ryan, Oct 27 2026): a material
// pickup (the Items / Source / Vendor fields), a custom load-up list, a load-up list from a template.
// Any mix — the pickup tile shows / hides its fields; the other two each add a list.
export function LoadUpTiles({ pickupOpen, onTogglePickup, onCustom, onTemplate }) {
  const [picking, setPicking] = useState(false);
  const tile = (on, color) => ({ flex: '1 1 180px', textAlign: 'left', padding: '10px 12px', borderRadius: 10, cursor: 'pointer', border: `1.5px solid ${on ? color : '#e5e7eb'}`, background: on ? color + '12' : '#fff' });
  const head = color => ({ fontSize: 13.5, fontWeight: 800, color });
  const sub = { fontSize: 11.5, color: '#6b7280', marginTop: 2 };
  return (
    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', position: 'relative' }}>
      <button type="button" onClick={onTogglePickup} style={tile(pickupOpen, '#1e3a5f')}>
        <div style={head('#1e3a5f')}>📦 Material Pickup {pickupOpen ? '✓' : ''}</div>
        <div style={sub}>Items, source, vendor — and a PO</div>
      </button>
      <button type="button" onClick={onCustom} style={tile(false, '#b45309')}>
        <div style={head('#b45309')}>✏️ Custom Load-up List</div>
        <div style={sub}>Type your own list</div>
      </button>
      <div style={{ flex: '1 1 180px', position: 'relative', display: 'flex' }}>
        <button type="button" onClick={() => setPicking(p => !p)} style={{ ...tile(picking, '#6d28d9'), flex: 1 }}>
          <div style={head('#6d28d9')}>📋 Load-up List from Templates</div>
          <div style={sub}>Start from a saved list, then change it</div>
        </button>
        {picking && <LoadUpPicker align="right" onPick={tpl => { setPicking(false); onTemplate(tpl); }} onClose={() => setPicking(false)} />}
      </div>
    </div>
  );
}

// One list. Lines change in place (click one), Enter adds a line (or paste a whole list). Two check
// marks a line — on a draft they wait for Save. 🧾 Create PO: tick the lines to buy, then make the PO.
function ListCard({ title, items, draft = false, role, canEdit, onRename, onRemove, onAddLines, onEditLine, onDelLine, onToggle, onCreatePo }) {
  const [text, setText] = useState('');
  const [editing, setEditing] = useState(null);   // { id, text }
  const [buy, setBuy] = useState(null);           // Set of line ids being put on a PO, while choosing
  const done = items.filter(i => i.checked).length;
  const fieldDone = items.filter(i => i.field_checked).length;
  const allDone = !draft && items.length > 0 && done === items.length && fieldDone === items.length;
  const shopOk = !draft && canEdit && canTickShop(role), fieldOk = !draft && canEdit && canTickField(role);

  function rename() {
    const t = window.prompt('Name this list:', title || '');
    if (t != null && t.trim() && t.trim() !== title) onRename(t.trim());
  }
  function remove() {
    if (window.confirm(`Remove "${title}"${items.length ? ` and its ${items.length} line${items.length === 1 ? '' : 's'}` : ''}?`)) onRemove();
  }
  function addLines() {
    const lines = splitLines(text);
    if (!lines.length) return;
    onAddLines(lines); setText('');
  }
  function saveEdit() {
    const e = editing; setEditing(null);
    if (e && e.text.trim() && e.text.trim() !== (items.find(i => i.id === e.id) || {}).text) onEditLine({ id: e.id }, e.text.trim());
  }
  const pickBuy = id => setBuy(s => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const box = { width: 34, display: 'flex', justifyContent: 'center', flexShrink: 0 };

  return (
    <div style={{ border: `1px solid ${allDone ? '#bbf7d0' : '#e5e7eb'}`, background: allDone ? '#f0fdf4' : '#fff', borderRadius: 10, padding: '10px 12px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 16 }}>{allDone ? '✅' : '📋'}</span>
        <button type="button" onClick={canEdit ? rename : undefined} title={canEdit ? 'Rename' : undefined}
          style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', fontSize: 14, fontWeight: 800, color: '#111827', cursor: canEdit ? 'pointer' : 'default', textAlign: 'left', flex: 1, minWidth: 120 }}>
          {title}
        </button>
        <span style={{ fontSize: 12, fontWeight: 800, color: allDone ? '#15803d' : '#6b7280', whiteSpace: 'nowrap' }}>
          {!items.length ? 'empty' : draft ? `${items.length} line${items.length === 1 ? '' : 's'}` : `Shop ${done}/${items.length} · Field ${fieldDone}/${items.length}`}
        </span>
        {onCreatePo && items.length > 0 && !buy && (
          <button type="button" onClick={() => setBuy(new Set())} title="Tick the lines the shop has to buy, then make the PO"
            style={{ ...btn, padding: '4px 10px', fontSize: 12, border: '1px solid #bfdbfe', background: '#eff6ff', color: '#1d4ed8' }}>🧾 Create PO</button>
        )}
        {canEdit && <button type="button" onClick={remove} title="Remove this list" style={{ background: 'none', border: 'none', color: '#c9ccd1', fontSize: 18, cursor: 'pointer', lineHeight: 1, padding: 0 }}>×</button>}
      </div>
      {!draft && items.length > 0 && (
        <div style={{ height: 4, background: '#e5e7eb', borderRadius: 2, overflow: 'hidden', marginBottom: 8 }}>
          <div style={{ width: `${Math.round(((done + fieldDone) / (2 * items.length)) * 100)}%`, height: '100%', background: allDone ? '#15803d' : '#e87722', transition: 'width .2s' }} />
        </div>
      )}
      {buy && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', margin: '0 0 8px', padding: '7px 10px', borderRadius: 8, background: '#eff6ff', border: '1px solid #bfdbfe', fontSize: 12.5, color: '#1e3a8a' }}>
          <span style={{ flex: 1 }}>Tick the lines the shop has to buy.</span>
          <button type="button" onClick={() => setBuy(new Set(items.filter(i => !i.po_number).map(i => i.id)))} style={{ ...btn, padding: '3px 9px', fontSize: 11.5 }}>All not on a PO</button>
          <button type="button" onClick={() => setBuy(null)} style={{ ...btn, padding: '3px 9px', fontSize: 11.5 }}>Cancel</button>
          <button type="button" disabled={!buy.size} onClick={() => { const picked = items.filter(i => buy.has(i.id)); setBuy(null); onCreatePo(picked); }}
            style={{ ...btn, padding: '3px 10px', fontSize: 11.5, border: 'none', background: buy.size ? '#1d4ed8' : '#cbd5e1', color: '#fff' }}>
            🧾 Make the PO{buy.size ? ` (${buy.size})` : ''}
          </button>
        </div>
      )}
      {items.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 4px 2px', fontSize: 10, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.3 }}>
          {buy && <span style={{ ...box, color: '#1d4ed8' }}>Buy</span>}
          <span style={{ ...box }} title="The shop ticks this when it's loaded">Shop</span>
          <span style={{ ...box }} title="The PM or field supervisor ticks this when they have it">Field</span>
          <span style={{ flex: 1 }}>{draft ? <span style={{ textTransform: 'none', fontWeight: 600, color: '#9ca3af' }}>check marks open up once the order is saved</span> : ''}</span>
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {items.map(it => (
          <div key={it.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 4px', borderRadius: 6 }}>
            {buy && <span style={box}><input type="checkbox" checked={buy.has(it.id)} onChange={() => pickBuy(it.id)} style={{ width: 18, height: 18, accentColor: '#1d4ed8', cursor: 'pointer' }} /></span>}
            <span style={box}>
              <input type="checkbox" checked={!!it.checked} disabled={!shopOk} onChange={() => onToggle && onToggle(it, 'checked')}
                title={draft ? 'Save the order first' : it.checked ? `Loaded — ${it.checked_by_name || ''}` : shopOk ? 'Shop: tick when it’s loaded' : 'The shop ticks this one'}
                style={{ width: 20, height: 20, cursor: shopOk ? 'pointer' : 'default', accentColor: '#e87722' }} />
            </span>
            <span style={box}>
              <input type="checkbox" checked={!!it.field_checked} disabled={!fieldOk} onChange={() => onToggle && onToggle(it, 'field_checked')}
                title={draft ? 'Save the order first' : it.field_checked ? `Have it — ${it.field_checked_by_name || ''}` : fieldOk ? 'PM / field supervisor: tick when you have it' : 'The PM or field supervisor ticks this one'}
                style={{ width: 20, height: 20, cursor: fieldOk ? 'pointer' : 'default', accentColor: '#15803d' }} />
            </span>
            {editing && editing.id === it.id ? (
              <input autoFocus value={editing.text} onChange={e => setEditing({ id: it.id, text: e.target.value })}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); saveEdit(); } if (e.key === 'Escape') setEditing(null); }}
                onBlur={saveEdit} style={{ ...inp, padding: '5px 8px', fontSize: 14, flex: 1 }} />
            ) : (
              <span onClick={canEdit ? () => setEditing({ id: it.id, text: it.text }) : undefined} title={canEdit ? 'Click to change' : undefined}
                style={{ flex: 1, minWidth: 0, fontSize: 14, color: it.checked && it.field_checked ? '#9ca3af' : '#111827', textDecoration: it.checked && it.field_checked ? 'line-through' : 'none', cursor: canEdit ? 'text' : 'default', lineHeight: 1.35 }}>
                {it.text}
              </span>
            )}
            {it.po_number && <span title="Bought on this PO" style={{ fontSize: 10.5, fontWeight: 800, color: '#1d4ed8', background: '#eff6ff', borderRadius: 999, padding: '1px 7px', whiteSpace: 'nowrap' }}>🧾 PO #{it.po_number}</span>}
            {(it.checked || it.field_checked) && <span style={{ fontSize: 10.5, color: '#9ca3af', whiteSpace: 'nowrap' }}>
              {[it.checked && it.checked_by_name ? `🚚 ${it.checked_by_name.split(' ')[0]}` : '', it.field_checked && it.field_checked_by_name ? `✓ ${it.field_checked_by_name.split(' ')[0]}` : ''].filter(Boolean).join(' · ')}
            </span>}
            {canEdit && <button type="button" onClick={() => onDelLine(it)} title="Remove this line" style={{ background: 'none', border: 'none', color: '#d1d5db', fontSize: 16, cursor: 'pointer', lineHeight: 1, padding: 0 }}>×</button>}
          </div>
        ))}
      </div>
      {canEdit && (
        <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
          <textarea value={text} onChange={e => setText(e.target.value)} rows={1} placeholder="Add a line — what & how many. Enter to add, or paste a whole list"
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); addLines(); } }}
            style={{ ...inp, resize: 'none', minHeight: 36 }} />
          <button type="button" onClick={addLines} disabled={!text.trim()} style={{ ...btn, background: text.trim() ? '#1e3a5f' : '#f3f4f6', color: text.trim() ? '#fff' : '#9ca3af', border: 'none' }}>Add</button>
        </div>
      )}
    </div>
  );
}

// Pick one of the saved load-up lists (Settings → Checklists & Load-up Lists). Sits under its button.
export function LoadUpPicker({ onPick, onClose, align = 'left' }) {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState('');
  useEffect(() => { apiFetch('/checklists?kind=loadup').then(r => setRows(Array.isArray(r) ? r : [])).catch(() => setRows([])); }, []);
  const ql = q.trim().toLowerCase();
  const shown = (rows || []).filter(r => !ql || `${r.title} ${r.category || ''} ${r.keywords || ''}`.toLowerCase().includes(ql));
  return (
    <div style={{ position: 'absolute', top: '100%', ...(align === 'right' ? { right: 0 } : { left: 0 }), marginTop: 4, zIndex: 50, width: 340, maxWidth: '90vw', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, boxShadow: '0 10px 30px rgba(0,0,0,0.15)', overflow: 'hidden' }}>
      <div style={{ display: 'flex', gap: 6, padding: 8, borderBottom: '1px solid #f3f4f6' }}>
        <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Search load-up lists…" style={{ ...inp, padding: '7px 9px', fontSize: 13 }} />
        <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: 18, cursor: 'pointer' }}>×</button>
      </div>
      <div style={{ maxHeight: 280, overflowY: 'auto' }}>
        {rows === null && <div style={{ padding: 12, fontSize: 12.5, color: '#9ca3af' }}>Loading…</div>}
        {rows && shown.length === 0 && <div style={{ padding: 12, fontSize: 12.5, color: '#9ca3af' }}>{rows.length ? 'No match.' : 'No load-up lists saved yet — add them in Settings → Checklists & Load-up Lists.'}</div>}
        {shown.map(r => (
          <button key={r.id} type="button" onClick={() => onPick(r)}
            style={{ display: 'block', width: '100%', textAlign: 'left', background: '#fff', border: 'none', borderBottom: '1px solid #f3f4f6', padding: '8px 12px', cursor: 'pointer' }}>
            <div style={{ fontSize: 13.5, fontWeight: 800, color: '#111827' }}>{r.title} <span style={{ fontSize: 11, fontWeight: 700, color: '#6b7280' }}>· {r.items.length} item{r.items.length === 1 ? '' : 's'}</span></div>
            <div style={{ fontSize: 11.5, color: '#6b7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.items.slice(0, 4).join(' · ')}{r.items.length > 4 ? ' …' : ''}</div>
          </button>
        ))}
      </div>
    </div>
  );
}
